// ConfirmModal — small confirmation dialog with optional note textarea.
// Mirrors `.modal-backdrop` + `.modal` + `.modal__actions` in
// /Users/harry/Downloads/employee-portal/project/styles.css (lines 1437–1461)
// and ConfirmModal in /Users/harry/Downloads/employee-portal/project/components.jsx
// (line 685).

import { Box, Heading, HStack, Text } from "@chakra-ui/react";
import { ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PloyButton, { PloyButtonVariant } from "./PloyButton";
import Textarea from "./Textarea";

export type ConfirmTone = "primary" | "success" | "danger";

export interface ConfirmModalProps {
  title: string;
  body?: ReactNode;
  /** When set, renders a textarea above the action row. */
  notePlaceholder?: string;
  confirmLabel: string;
  /** Label of the dismiss action. Default "Cancel" — override it whenever
   *  "Cancel" would read as a second way to perform the destructive action
   *  (e.g. "Keep request" next to "Cancel request"). */
  cancelLabel?: string;
  /** Visual tone of the confirm button. Default "primary". */
  confirmTone?: ConfirmTone;
  /** Receives the (trimmed-safe) note value at confirm time. */
  onConfirm: (note: string) => void;
  onCancel: () => void;
  /** When true + notePlaceholder set, confirm is disabled until note non-empty. */
  requireNote?: boolean;
  /** Caps the note's length (the server's own limit, where it has one). */
  noteMaxLength?: number;
  /** The confirm is in flight: its button spins and the dialog can't be
   *  dismissed, so the note survives until the caller closes it. */
  isConfirming?: boolean;
  /** Why the last confirm was refused, shown above the actions. */
  error?: ReactNode;
}

// PloyButton variants are a superset of ConfirmTone — primary/success/danger
// share names, so the cast is safe.
const TONE_TO_VARIANT: Record<ConfirmTone, PloyButtonVariant> = {
  primary: "primary",
  success: "success",
  danger: "danger",
};

// Above Chakra's `modal` layer (1400) so a confirmation opened from inside a
// Drawer/Modal lands on top of it instead of being buried under that overlay,
// and below `toast` (1700) so a resulting toast still reads.
const CONFIRM_Z_INDEX = 1500;

const ConfirmModal = ({
  title,
  body,
  notePlaceholder,
  confirmLabel,
  cancelLabel = "Cancel",
  confirmTone = "primary",
  onConfirm,
  onCancel,
  requireNote = false,
  noteMaxLength,
  isConfirming = false,
  error,
}: ConfirmModalProps) => {
  const [note, setNote] = useState("");
  const confirmDisabled =
    requireNote && !!notePlaceholder && note.trim().length === 0;

  // Focus the confirm action on open (unless there's a note to fill in first),
  // so the dialog can be confirmed straight away with Enter.
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!notePlaceholder) confirmRef.current?.focus();
  }, [notePlaceholder]);

  // Escape always cancels, regardless of where focus sits, unless a confirm
  // is in flight.
  const cancel = () => {
    if (!isConfirming) onCancel();
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isConfirming) onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, isConfirming]);

  // Rendered into document.body rather than in place. A `position: fixed`
  // element is positioned against the nearest ancestor that has a transform,
  // filter or backdrop-filter — not the viewport — so an in-place dialog
  // shrinks into whichever card happens to be hover-lifted or animating, and
  // flickers back to full screen the moment that transform drops (which is
  // exactly what Firefox does when the pointer leaves the window). The portal
  // takes the dialog out of every such subtree.
  const dialog = (
    <Box
      // .modal-backdrop — fixed overlay with blur + soft tint.
      position="fixed"
      inset={0}
      zIndex={CONFIRM_Z_INDEX}
      display="flex"
      alignItems="center"
      justifyContent="center"
      p="1xl" // 24px
      bg="linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.15) 100%)"
      sx={{
        backdropFilter: "blur(8px) saturate(120%)",
        WebkitBackdropFilter: "blur(8px) saturate(120%)",
        "@keyframes ployModalFadeIn": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
      animation="ployModalFadeIn 200ms ease-out"
      onClick={cancel}
      role="presentation"
    >
      <Box
        // .modal — fixed-width card with deep shadow.
        width="396px"
        maxW="100%"
        bg="neutral.25"
        borderRadius="component.4xl" // 16px
        p="1xl" // 24px
        boxShadow="modal"
        display="flex"
        flexDirection="column"
        gap="lg" // 16px — matches CSS `gap: 16px`
        // Scale in from 0.96 (never 0). Modals stay centered, so the origin is
        // center (not trigger-anchored). Strong ease-out curve, snappy.
        transformOrigin="center"
        sx={{
          "@keyframes ployModalCardIn": {
            from: { opacity: 0, transform: "scale(0.96)" },
            to: { opacity: 1, transform: "scale(1)" },
          },
        }}
        animation="ployModalCardIn 200ms cubic-bezier(0.23, 1, 0.32, 1)"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ploy-confirm-modal-title"
        fontFamily="body"
      >
        {/* Title: Hubot heading, 20px / weight 500 (theme `xl`). */}
        <Heading
          id="ploy-confirm-modal-title"
          as="h3"
          m={0}
          fontFamily="heading"
          fontWeight={500}
          fontSize="xl" // 20px
          color="neutral.1000"
          lineHeight="1.3"
        >
          {title}
        </Heading>

        {body && (
          <Text
            m={0}
            mt="1xs" // 6px
            mb="md" // 12px
            fontSize="smd" // 13px
            color="neutral.700"
            lineHeight="1.5"
          >
            {body}
          </Text>
        )}

        {notePlaceholder && (
          <Textarea
            placeholder={notePlaceholder}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            aria-label={notePlaceholder}
            maxLength={noteMaxLength}
            autoFocus
          />
        )}

        {error && (
          <Text m={0} fontSize="smd" color="error.600" lineHeight="1.5">
            {error}
          </Text>
        )}

        {/* .modal__actions — flex-end row, 8px gap, 24px top margin. */}
        <HStack justifyContent="flex-end" spacing="sm" mt="1xl">
          <PloyButton
            variant="secondary"
            onClick={cancel}
            isDisabled={isConfirming}
          >
            {cancelLabel}
          </PloyButton>
          <PloyButton
            ref={confirmRef}
            variant={TONE_TO_VARIANT[confirmTone]}
            onClick={() => onConfirm(note)}
            isDisabled={confirmDisabled}
            isLoading={isConfirming}
          >
            {confirmLabel}
          </PloyButton>
        </HStack>
      </Box>
    </Box>
  );

  return createPortal(dialog, document.body);
};

export default ConfirmModal;
